import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import AnalysisModule from '../components/AnalysisModule';
import ChartCard from '../components/ChartCard';
import ExpandableCard from '../components/ExpandableCard';
import History from '../components/History';
import { initFirebase, saveAnalysis, subscribeToHistory } from '../firebase';

const Waltz = () => {
  const [userId, setUserId] = useState(null);
  const [authStatus, setAuthStatus] = useState('Cargando autenticación...');
  const [analyses, setAnalyses] = useState([]);

  useEffect(() => {
    const init = async () => {
      const uid = await initFirebase();
      if (uid) {
        setUserId(uid);
        setAuthStatus(`✅ Conectado`);
        
        const unsubscribe = subscribeToHistory(uid, (data) => {
          setAnalyses(data);
        });
        
        return () => unsubscribe();
      } else {
        setAuthStatus('⚠️ No hay usuario autenticado');
      }
    };
    
    init();
  }, []);

  const handleSave = (query, result, type) => {
    if (userId) {
      saveAnalysis(userId, query, result, type);
    }
  };

  const balanceData = {
    labels: ['Bloque de Poder A', 'Bloque de Poder B'],
    datasets: [{
      label: 'Distribución de Poder',
      data: [50, 50],
      backgroundColor: ['#004e92', '#4286f4'],
      borderColor: '#ffffff',
      borderWidth: 4,
    }]
  };

  const polarityData = {
    labels: ['Sistema Multipolar', 'Sistema Unipolar', 'Sistema Bipolar'],
    datasets: [{
      label: 'Nivel de Estabilidad',
      data: [30, 65, 90],
      backgroundColor: ['#9BBFF2', '#4286f4', '#004e92'],
      borderColor: '#000428',
      borderWidth: 2,
    }]
  };

  return (
    <div className="container">
      <div style={{ marginBottom: '1rem' }}>
        <Link to="/">
          <button style={{
            padding: '0.5rem 1rem',
            borderRadius: '0.5rem',
            border: '1px solid rgba(148, 163, 184, 0.3)',
            background: 'rgba(30, 41, 59, 0.8)',
            color: '#cbd5e1',
            cursor: 'pointer',
            fontSize: '0.9rem'
          }}>
            ← Volver al Inicio
          </button>
        </Link>
      </div>
      <Header 
        title="Teoría de la Política Internacional" 
        subtitle="Una Visualización de las Ideas de Kenneth N. Waltz"
        authStatus={authStatus}
      />

      <section className="card" style={{ textAlign: 'center', marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#004e92' }}>La Tesis Central</h2>
        <p style={{ fontSize: '4rem', fontWeight: 'black', margin: '1rem 0', color: '#000428' }}>ANARQUÍA</p>
        <p style={{ maxWidth: '800px', margin: '0 auto', lineHeight: '1.7' }}>
          El concepto fundamental de Waltz no es el caos, sino la <strong>ausencia de una autoridad central</strong> o un gobierno mundial. Esta estructura anárquica es la fuerza que moldea el comportamiento de los Estados.
        </p>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center', marginBottom: '2rem', color: '#000428' }}>
          Los 3 Pilares de la Estructura del Sistema
        </h2>
        <div className="grid">
          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🌐</div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#004e92' }}>
              Principio Ordenador
            </h3>
            <p>La <strong>anarquía</strong> es la característica definitoria del sistema. No hay una jerarquía superior a los Estados.</p>
          </div>
          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🏛️</div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#004e92' }}>
              Carácter de las Unidades
            </h3>
            <p>Los Estados son <strong>funcionalmente indiferenciados</strong>. Todos persiguen el mismo objetivo primordial: la <strong>supervivencia</strong>.</p>
          </div>
          <div className="card" style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📊</div>
            <h3 style={{ fontSize: '1.25rem', fontWeight: 'bold', marginBottom: '0.5rem', color: '#004e92' }}>
              Distribución de Capacidades
            </h3>
            <p>La única variable estructural es la <strong>polaridad</strong>, es decir, cómo se distribuye el poder entre las grandes potencias.</p>
          </div>
        </div>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center', marginBottom: '2rem', color: '#004e92' }}>
          Consecuencias Inevitables de la Anarquía
        </h2>
        <div className="grid">
          <ExpandableCard
            icon="🔄"
            title="El Dilema de la Seguridad"
            color="#ef4444"
            summary="Las acciones que un Estado toma para aumentar su propia seguridad son vistas como una amenaza por otros, creando un ciclo vicioso."
            details={
              <div>
                <p style={{ fontWeight: 'bold', marginBottom: '0.75rem' }}>El Ciclo del Dilema:</p>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ padding: '0.75rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: '0.5rem', width: '100%', textAlign: 'center', borderLeft: '3px solid #3b82f6' }}>
                    <strong>Paso 1:</strong> Estado A aumenta su poder militar para sentirse más seguro
                  </div>
                  <div style={{ fontSize: '1.5rem', color: '#ef4444' }}>⬇️</div>
                  <div style={{ padding: '0.75rem', backgroundColor: 'rgba(239, 68, 68, 0.1)', borderRadius: '0.5rem', width: '100%', textAlign: 'center', borderLeft: '3px solid #ef4444' }}>
                    <strong>Paso 2:</strong> Estado B percibe esto como una amenaza
                  </div>
                  <div style={{ fontSize: '1.5rem', color: '#ef4444' }}>⬇️</div>
                  <div style={{ padding: '0.75rem', backgroundColor: 'rgba(234, 179, 8, 0.1)', borderRadius: '0.5rem', width: '100%', textAlign: 'center', borderLeft: '3px solid #eab308' }}>
                    <strong>Paso 3:</strong> Estado B aumenta su propio poder militar
                  </div>
                  <div style={{ fontSize: '1.5rem', color: '#ef4444' }}>⬇️</div>
                  <div style={{ padding: '0.75rem', backgroundColor: 'rgba(139, 92, 246, 0.1)', borderRadius: '0.5rem', width: '100%', textAlign: 'center', borderLeft: '3px solid #8b5cf6' }}>
                    <strong>Paso 4:</strong> Estado A ahora se siente menos seguro y el ciclo se reinicia
                  </div>
                </div>
                <p style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'rgba(239, 68, 68, 0.1)', borderRadius: '0.5rem' }}>
                  ⚠️ <strong>Resultado:</strong> Carrera armamentista inevitable, incluso cuando ningún Estado tiene intenciones agresivas.
                </p>
              </div>
            }
          />

          <ExpandableCard
            icon="🤝"
            title="Sistema de Autoayuda (Self-Help)"
            color="#3b82f6"
            summary="En ausencia de una autoridad central, cada Estado debe confiar solo en sí mismo para su supervivencia."
            details={
              <div>
                <p style={{ fontWeight: 'bold', marginBottom: '0.75rem' }}>Implicaciones del Self-Help:</p>
                <ul style={{ marginLeft: '1.5rem', lineHeight: '1.8' }}>
                  <li><strong>No hay 911 internacional:</strong> Ninguna autoridad superior protegerá a un Estado amenazado</li>
                  <li><strong>Desconfianza estructural:</strong> No se puede confiar en las promesas de otros Estados</li>
                  <li><strong>Prioridad a la supervivencia:</strong> La seguridad nacional es el objetivo primordial</li>
                  <li><strong>Equilibrio de poder automático:</strong> Los Estados se alían para contrarrestar amenazas</li>
                </ul>
                <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: '0.5rem', borderLeft: '3px solid #3b82f6' }}>
                  <p style={{ fontWeight: 'bold', color: '#3b82f6' }}>💡 Ejemplo Histórico:</p>
                  <p style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
                    Durante la Guerra Fría, EE.UU. y la URSS no podían confiar en tratados sin verificación. Cada uno debía asegurar su propia supervivencia mediante disuasión nuclear.
                  </p>
                </div>
              </div>
            }
          />
        </div>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center', marginBottom: '2rem', color: '#004e92' }}>
          Gráficos del Sistema Internacional
        </h2>
        <div className="grid">
          <ChartCard
            title="Equilibrio de Poder"
            description="En un sistema de autoayuda, los Estados forman equilibrios de poder automáticamente."
            type="doughnut"
            data={balanceData}
            options={{ responsive: true, maintainAspectRatio: false }}
          />
          
          <ChartCard
            title="Estabilidad según Polaridad"
            description="Waltz argumentó que los sistemas bipolares son más estables."
            type="bar"
            data={polarityData}
            options={{ 
              indexAxis: 'y',
              responsive: true, 
              maintainAspectRatio: false,
              scales: {
                x: { beginAtZero: true, max: 100 }
              }
            }}
          />
        </div>
      </section>

      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '2rem', fontWeight: 'bold', textAlign: 'center', marginBottom: '2rem', color: '#004e92' }}>
          Polaridad y Estabilidad
        </h2>
        <div className="grid">
          <ExpandableCard
            icon="⚖️"
            title="Sistema Bipolar (Más Estable)"
            color="#22c55e"
            summary="Waltz argumentó que los sistemas con dos superpotencias son inherentemente más estables y menos propensos a grandes guerras."
            details={
              <div>
                <p style={{ fontWeight: 'bold', marginBottom: '0.75rem' }}>¿Por qué es más estable?</p>
                <ul style={{ marginLeft: '1.5rem', lineHeight: '1.8' }}>
                  <li><strong>Claridad:</strong> Cada superpotencia sabe quién es su rival principal</li>
                  <li><strong>Responsabilidad:</strong> Solo dos actores responsables del equilibrio</li>
                  <li><strong>Disuasión efectiva:</strong> Cada uno puede contrarrestar al otro</li>
                  <li><strong>Menos incertidumbre:</strong> Menos cálculos complejos sobre alianzas</li>
                </ul>
                <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'rgba(34, 197, 94, 0.1)', borderRadius: '0.5rem', borderLeft: '3px solid #22c55e' }}>
                  <p style={{ fontWeight: 'bold', color: '#22c55e' }}>✓ Ejemplo: Guerra Fría (1947-1991)</p>
                  <p style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
                    A pesar de la tensión extrema, EE.UU. y la URSS nunca entraron en guerra directa. El sistema bipolar funcionó como Waltz predijo.
                  </p>
                </div>
              </div>
            }
          />

          <ExpandableCard
            icon="🌐"
            title="Sistema Multipolar (Menos Estable)"
            color="#eab308"
            summary="Sistemas con múltiples grandes potencias son más propensos a errores de cálculo y guerras."
            details={
              <div>
                <p style={{ fontWeight: 'bold', marginBottom: '0.75rem' }}>¿Por qué es menos estable?</p>
                <ul style={{ marginLeft: '1.5rem', lineHeight: '1.8' }}>
                  <li><strong>Incertidumbre:</strong> Difícil predecir quién se aliará con quién</li>
                  <li><strong>Cálculos complejos:</strong> Múltiples amenazas potenciales</li>
                  <li><strong>Responsabilidad difusa:</strong> Nadie se siente totalmente responsable</li>
                  <li><strong>Tentación de agresión:</strong> Oportunidades para ganar ventaja</li>
                </ul>
                <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'rgba(234, 179, 8, 0.1)', borderRadius: '0.5rem', borderLeft: '3px solid #eab308' }}>
                  <p style={{ fontWeight: 'bold', color: '#eab308' }}>⚠️ Ejemplo: Europa 1914</p>
                  <p style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
                    El sistema multipolar europeo (Alemania, Francia, Rusia, Austria-Hungría, Reino Unido) colapsó en la Primera Guerra Mundial debido a errores de cálculo sobre alianzas.
                  </p>
                </div>
              </div>
            }
          />

          <ExpandableCard
            icon="👑"
            title="Sistema Unipolar (Inestable a Largo Plazo)"
            color="#8b5cf6"
            summary="Un sistema con una sola superpotencia dominante tiende a generar resistencia y eventualmente colapsar."
            details={
              <div>
                <p style={{ fontWeight: 'bold', marginBottom: '0.75rem' }}>Problemas del Unipolarismo:</p>
                <ul style={{ marginLeft: '1.5rem', lineHeight: '1.8' }}>
                  <li><strong>Tentación imperial:</strong> La superpotencia puede sobreextenderse</li>
                  <li><strong>Balancing inevitable:</strong> Otros Estados se alían contra el hegemón</li>
                  <li><strong>Falta de restricciones:</strong> Nadie puede contrarrestar al hegemón</li>
                  <li><strong>Resentimiento:</strong> Otros Estados buscan desafiar el orden</li>
                </ul>
                <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: 'rgba(139, 92, 246, 0.1)', borderRadius: '0.5rem', borderLeft: '3px solid #8b5cf6' }}>
                  <p style={{ fontWeight: 'bold', color: '#8b5cf6' }}>🔮 Ejemplo: EE.UU. Post-Guerra Fría (1991-presente)</p>
                  <p style={{ fontSize: '0.9rem', marginTop: '0.25rem' }}>
                    Tras el colapso soviético, EE.UU. fue la única superpotencia. Pero China y Rusia han emergido como contrapesos, validando la predicción de Waltz sobre el balancing.
                  </p>
                </div>
              </div>
            }
          />
        </div>
      </section>

      <AnalysisModule
        title="✨ Análisis Estructural Neorrealista"
        description="Introduzca un evento o escenario hipotético en la política internacional."
        inputType="textarea"
        category="waltz"
        onSave={handleSave}
        type="scenario"
      />

      <AnalysisModule
        title="📚 Definición de Conceptos Clave"
        description="Seleccione un concepto para obtener su definición desde la óptica de Waltz."
        inputType="select"
        options={[
          'Neorealism',
          'Balance of Power'
        ]}
        category="waltz"
        onSave={handleSave}
        type="concept"
      />

      <History analyses={analyses} />

      <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 1000 }}>
        <Link to="/" className="btn btn-primary" style={{ textDecoration: 'none' }}>
          🏠 Inicio
        </Link>
      </div>
    </div>
  );
};

export default Waltz;
